/* "Goes with this" — the parts, assembly and freeze bag for this device.
 *
 * On a complete assembly: every repair part made for the same brand + model +
 * size, grouped (rebuild kits, checks, relief valve, …), plus a freeze bag
 * inquiry. On a repair part: the other parts for the same device and size, the
 * assembly itself, and the same freeze bag inquiry. Each part links to its
 * own page, which shows its own matches — related, never another size.
 *
 * The customer ticks what they need, then either:
 *   - "Add to my quote": everything ticked (the assembly, parts, freeze bag)
 *     goes to the quote drawer as one request; or
 *   - "Buy now": the ticked parts that can be bought today go to the cart.
 * Assemblies, quote-only parts and the freeze bag never go to the cart. The
 * app refuses a quote made only of buy-now parts, so the quote button needs
 * at least one thing that has to be quoted.
 *
 * Data: GET {api}/api/storefront/goes-with?product_id= (see the app's
 * src/lib/goes-with.ts for the matching and grouping rules).
 */
if (!customElements.get('goes-with')) {
  const BAG_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 2v3M12 19v3M4.2 5.6l2.1 2.1M17.7 16.3l2.1 2.1M2 12h3M19 12h3M4.2 18.4l2.1-2.1M17.7 7.7l2.1-2.1"/><circle cx="12" cy="12" r="4"/></svg>';
  const PLACEHOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v6M12 16v6M2 12h6M16 12h6"/></svg>';

  class GoesWith extends HTMLElement {
    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.self = {
        variantId: this.dataset.variantId,
        title: this.dataset.title || '',
        image: this.dataset.image || '',
        handle: this.dataset.handle || '',
        sku: this.dataset.sku || '',
      };
      this.checked = new Set();
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/goes-with?product_id=${encodeURIComponent(this.dataset.productId)}`);
        const data = await res.json();
        const g = data && data.goesWith;
        const hasParts = g && (g.groups.length || g.assemblies.length);
        if (!g || (!hasParts && g.anchor.kind !== 'assembly')) { this.remove(); return; }
        this.g = g;
        this.items = new Map(); // key -> item
        if (g.anchor.kind === 'assembly' && this.self.variantId) {
          this.items.set('self', { key: 'self', kind: 'assembly', variantId: this.self.variantId, title: this.self.title, sku: this.self.sku, image: this.self.image, handle: this.self.handle, quoteOnly: true });
          this.checked.add('self');
        }
        for (const grp of g.groups) for (const p of grp.products) this.items.set(`p${p.id}`, this.itemFor(p));
        this.items.set('bag', { key: 'bag', kind: 'bag', variantId: `inquiry:freeze-bag:${g.anchor.id}`, title: g.freezeBag.title, quoteOnly: true });
        this.render();
        this.addJumpButton();
      } catch (e) {
        this.remove();
      }
    }

    itemFor(p) {
      const v = (p.variants || [])[0] || {};
      return {
        key: `p${p.id}`, kind: 'part', product: p,
        variantId: p.variants.length === 1 ? v.variantId : null,
        multi: p.variants.length !== 1,
        title: p.title, sku: v.sku || '', image: p.imageUrl || '', handle: p.handle,
        quoteOnly: p.quoteOnly === true, available: !!p.available,
      };
    }

    /**
     * A "see the parts" button under the buy / quote button at the top of
     * the page, for products that actually have parts. Added from here (not the
     * product template) so it only ever appears when this section has
     * something to show.
     */
    addJumpButton() {
      const partCount = this.g.groups.reduce((n, grp) => n + grp.products.length, 0);
      if (!partCount || document.querySelector('.goes-with-jump')) return;
      const form = document.querySelector('.js-product-form-main');
      const block = form && form.closest('.product-info__block');
      if (!block) return;
      // A repair part is not a device, so its button says "related" (owner, 2026-09-23).
      const label = this.g.anchor.kind === 'assembly'
        ? `See the ${partCount} repair part${partCount === 1 ? '' : 's'} for this assembly`
        : `See ${partCount} related repair part${partCount === 1 ? '' : 's'}`;
      const wrap = document.createElement('div');
      wrap.className = 'product-info__block goes-with-jump';
      wrap.innerHTML = `<button type="button" class="goes-with-jump__btn">${this.esc(label)} <span aria-hidden="true">↓</span></button>`;
      wrap.querySelector('button').addEventListener('click', () => this.scrollToSelf());
      block.after(wrap);
    }

    /** Glide down to this section, clear of the sticky header. If the glide
        stalls (the sticky product column can interrupt it), finish the jump. */
    scrollToSelf() {
      const header = document.querySelector('.cc-header--sticky, sticky-header, .shopify-section-header');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const top = () => Math.max(0, this.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top: top(), behavior: 'smooth' });
      setTimeout(() => {
        if (Math.abs(window.scrollY - top()) > 40) window.scrollTo(0, top());
      }, 1100);
    }

    /* ---- What the two buttons would do with the current ticks ---- */

    selection() {
      const picked = [...this.checked].map((k) => this.items.get(k)).filter(Boolean);
      const forQuote = picked.filter((it) => it.variantId);
      const forCart = picked.filter((it) => it.kind === 'part' && !it.quoteOnly && it.available && it.variantId);
      const needsQuote = picked.some((it) => it.quoteOnly);
      return { picked, forQuote, forCart, needsQuote };
    }

    /* ---- Render ---- */

    render() {
      const g = this.g;
      const isAssembly = g.anchor.kind === 'assembly';
      const heading = isAssembly
        ? `Everything for your ${this.esc(g.anchor.deviceLabel)}`
        : `Related parts for the ${this.esc(g.anchor.deviceLabel)}`;
      const intro = isAssembly
        ? 'The repair parts made for this assembly, and a freeze bag to protect it. Tick what you need, then send it all as one quote, or buy the in-stock parts right now.'
        : 'Other parts for the same valve model and size. Tick what you need, then buy the in-stock ones now or send them to us as a quote.';

      const assemblies = !isAssembly && g.assemblies.length
        ? `<div class="goes-with__group">
            <h3 class="goes-with__group-title">Need the whole device?</h3>
            ${g.assemblies.map((a) => `
              <div class="goes-with__row goes-with__row--link">
                ${this.thumb(a.imageUrl, `/products/${this.esc(a.handle)}`)}
                <div class="goes-with__info">
                  <a class="goes-with__title" href="/products/${this.esc(a.handle)}">${this.esc(a.title)}</a>
                  <p class="goes-with__meta">Complete assembly · quoted by our team</p>
                </div>
                <a class="goes-with__view" href="/products/${this.esc(a.handle)}">View &amp; request a quote →</a>
              </div>`).join('')}
          </div>`
        : '';

      const selfRow = isAssembly && this.items.has('self')
        ? `<div class="goes-with__group">
            <h3 class="goes-with__group-title">The assembly</h3>
            ${this.row(this.items.get('self'), { meta: 'Complete assembly · quoted by our team', price: 'Pricing by quote' })}
          </div>`
        : '';

      const groups = g.groups.map((grp) => `
        <div class="goes-with__group">
          <h3 class="goes-with__group-title">${this.esc(grp.label)}</h3>
          ${grp.products.map((p) => this.row(this.items.get(`p${p.id}`))).join('')}
        </div>`).join('');

      const noParts = isAssembly && !g.groups.length
        ? '<p class="goes-with__empty">We don’t have repair parts listed for this exact model and size yet — ask us and we’ll find them.</p>'
        : '';

      const bag = `
        <div class="goes-with__group">
          <h3 class="goes-with__group-title">Freeze protection</h3>
          ${this.row(this.items.get('bag'), {
            meta: 'Tell us you want one and our team will match the right bag to this device. Standard bags for smaller devices can also be bought on the <a href="/pages/freeze-bags">Freeze Bags page</a>.',
            price: 'Pricing by quote',
            icon: BAG_ICON,
          })}
        </div>`;

      this.innerHTML = `
        <div class="goes-with__inner">
          <h2 class="goes-with__heading">${heading}</h2>
          <p class="goes-with__intro">${intro}</p>
          ${selfRow}${assemblies}${groups}${noParts}${bag}
          <div class="goes-with__actions" data-actions></div>
        </div>`;
      this.renderActions();
      this.bind();
    }

    row(it, opts = {}) {
      if (!it) return '';
      const p = it.product;
      const url = it.handle ? `/products/${this.esc(it.handle)}` : null;
      const price = opts.price
        || (it.quoteOnly ? 'Pricing by quote'
          : p && p.priceMin > 0 ? (p.priceMax > p.priceMin ? `From $${p.priceMin.toFixed(2)}` : `$${p.priceMin.toFixed(2)}`) : 'Pricing by quote');
      const status = it.kind !== 'part' ? ''
        : it.multi ? '<span class="goes-with__tag">Choose options on its page</span>'
        : it.quoteOnly ? '<span class="goes-with__tag">Quote only</span>'
        : it.available ? '<span class="goes-with__tag goes-with__tag--ok">In stock</span>'
        : '<span class="goes-with__tag">Out of stock — we can quote it</span>';
      const box = it.variantId
        ? `<input type="checkbox" class="goes-with__check" data-key="${it.key}"${this.checked.has(it.key) ? ' checked' : ''} aria-label="Select ${this.esc(it.title)}">`
        : '<span class="goes-with__check goes-with__check--none"></span>';
      const meta = opts.meta || [it.sku ? `SKU ${this.esc(it.sku)}` : '', status].filter(Boolean).join(' · ');
      return `<label class="goes-with__row">
          ${box}
          ${opts.icon ? `<span class="goes-with__thumb goes-with__thumb--icon">${opts.icon}</span>` : this.thumb(it.image, null)}
          <span class="goes-with__info">
            ${url ? `<a class="goes-with__title" href="${url}">${this.esc(it.title)}</a>` : `<span class="goes-with__title">${this.esc(it.title)}</span>`}
            <span class="goes-with__meta">${meta}</span>
          </span>
          <span class="goes-with__price${it.quoteOnly ? ' is-quote' : ''}">${price}</span>
        </label>`;
    }

    // PERF-4 (2026-10-21): the app sends full-size Shopify file URLs (some 120-250 KB each)
    // for thumbnails that draw at 56-72 px. Ask Shopify's CDN for a small copy instead
    // (width param), with 1x/2x/3x choices, and fixed width/height so nothing shifts.
    // Non-Shopify URLs are left untouched.
    sized(url, w) {
      if (!/^(https?:)?\/\/(cdn\.shopify\.com|[^/]+\/cdn\/shop)\//.test(url)) return null;
      const u = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
      return `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
    }

    thumb(image, href) {
      let inner = PLACEHOLDER;
      if (image) {
        const s1 = this.sized(image, 72);
        inner = s1
          ? `<img src="${this.esc(this.sized(image, 144))}" srcset="${this.esc(s1)} 1x, ${this.esc(this.sized(image, 144))} 2x, ${this.esc(this.sized(image, 216))} 3x" width="72" height="72" alt="" loading="lazy" decoding="async">`
          : `<img src="${this.esc(image)}" alt="" loading="lazy">`;
      }
      return href
        ? `<a class="goes-with__thumb" href="${href}" tabindex="-1" aria-hidden="true">${inner}</a>`
        : `<span class="goes-with__thumb">${inner}</span>`;
    }

    renderActions() {
      const el = this.querySelector('[data-actions]');
      if (!el) return;
      const { picked, forQuote, forCart, needsQuote } = this.selection();
      const stayOut = picked.length - forCart.length;
      const quoteHint = !picked.length ? 'Tick the items you want.'
        : !needsQuote ? 'Everything ticked can be bought now — use Buy now, or add the freeze bag to ask for a quote.'
        : '';
      const cartHint = !picked.length ? ''
        : !forCart.length ? 'Nothing ticked can go straight to the cart.'
        : stayOut > 0 ? `${stayOut} ticked item${stayOut === 1 ? '' : 's'} need${stayOut === 1 ? 's' : ''} a quote and won’t go in the cart.`
        : '';
      el.innerHTML = `
        <div class="goes-with__action">
          <button type="button" class="btn btn--primary" data-quote${forQuote.length && needsQuote ? '' : ' disabled'}>Add ${forQuote.length || ''} to my quote</button>
          <p class="goes-with__hint">${quoteHint || 'One request for everything ticked — our team confirms availability and pricing, then emails you an estimate.'}</p>
        </div>
        <div class="goes-with__action">
          <button type="button" class="btn btn--secondary" data-cart${forCart.length ? '' : ' disabled'}>Buy now — add ${forCart.length || ''} part${forCart.length === 1 ? '' : 's'} to cart</button>
          <p class="goes-with__hint">${cartHint || 'Need it now? In-stock parts check out today.'}</p>
        </div>`;
      el.querySelector('[data-quote]').addEventListener('click', () => this.addToQuote());
      el.querySelector('[data-cart]').addEventListener('click', (e) => this.addToCart(e.currentTarget));
    }

    bind() {
      this.querySelectorAll('.goes-with__check[data-key]').forEach((box) => box.addEventListener('change', () => {
        if (box.checked) this.checked.add(box.dataset.key); else this.checked.delete(box.dataset.key);
        this.renderActions();
      }));
    }

    /* ---- Actions ---- */

    addToQuote() {
      const { forQuote } = this.selection();
      for (const it of forQuote) {
        document.dispatchEvent(new CustomEvent('quote:add', { detail: {
          variantId: it.variantId, title: it.title, sku: it.sku || null,
          image: it.image || null, handle: it.handle || null, quantity: 1,
        } }));
      }
    }

    async addToCart(button) {
      const { forCart } = this.selection();
      if (!forCart.length) return;
      const routes = (window.theme && theme.routes) || {};
      const cartAdd = routes.cartAdd || '/cart/add';
      button.disabled = true;
      button.classList.add('is-loading');
      try {
        const res = await fetch(`${cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: forCart.map((it) => ({ id: Number(it.variantId), quantity: 1 })) }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to cart');
        }
        const drawer = document.querySelector('cart-drawer');
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const s = await fetch('?sections=cart-icon-bubble').then((r) => r.json()).catch(() => null);
          if (s && s['cart-icon-bubble']) bubble.innerHTML = s['cart-icon-bubble'];
        }
        if (drawer && typeof drawer.refresh === 'function') {
          await drawer.refresh(true);
          drawer.open();
        } else {
          window.location.href = routes.cart || '/cart';
        }
      } catch (e) {
        const hint = button.parentElement.querySelector('.goes-with__hint');
        if (hint) hint.textContent = `${e.message}. Please try again or call (916) 251-7071.`;
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    esc(s) {
      return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }
  customElements.define('goes-with', GoesWith);
}
